/**
 * Artifact Types for Maxi AI - Extensible artifact system
 */

// Table-related types (inlined to avoid import restrictions)
export interface TableCell {
  content: string;
  highlight_cell: boolean;
}

export interface TableRow {
  cells: TableCell[];
}

export interface TableHeader {
  content: string;
  column_size: 'small' | 'medium' | 'large';
}

export interface TableData {
  headers: TableHeader[];
  rows: TableRow[];
}

// Base artifact interface
export interface BaseArtifact {
  id: string;
  type: string;
  status: 'processing' | 'complete' | 'failed';
  error?: string;
}

// Specific artifact types
export interface TableArtifact extends BaseArtifact {
  type: 'table';
  content?: TableData;
}

export interface LineChartArtifact extends BaseArtifact {
  type: 'line_chart';
  content?: {
    title?: string;
    data: Array<{
      x: string | number;
      y: number;
      series?: string;
    }>;
    xAxisLabel?: string;
    yAxisLabel?: string;
  };
}

export interface BarChartArtifact extends BaseArtifact {
  type: 'bar_chart';
  content?: {
    title?: string;
    data: Array<{
      category: string;
      value: number;
      series?: string;
    }>;
    xAxisLabel?: string;
    yAxisLabel?: string;
  };
}

// Union type for all artifacts
export type Artifact = TableArtifact | LineChartArtifact | BarChartArtifact;

// Artifact content types
export type ArtifactContent =
  | TableData
  | LineChartArtifact['content']
  | BarChartArtifact['content'];

// Type guards
export const isTableArtifact = (
  artifact: Artifact
): artifact is TableArtifact => {
  return artifact.type === 'table';
};

export const isLineChartArtifact = (
  artifact: Artifact
): artifact is LineChartArtifact => {
  return artifact.type === 'line_chart';
};

export const isBarChartArtifact = (
  artifact: Artifact
): artifact is BarChartArtifact => {
  return artifact.type === 'bar_chart';
};

// Artifact registry for easy extension
export const ARTIFACT_TYPES = {
  TABLE: 'table',
  LINE_CHART: 'line_chart',
  BAR_CHART: 'bar_chart',
} as const;

export type ArtifactType = (typeof ARTIFACT_TYPES)[keyof typeof ARTIFACT_TYPES];
